import React from 'react';
import {
    FilterDatatable,
    FiltersDatatableList,
    SelectApiFilter,
    StaticSelectFilter,
} from '@europrocurement/flexy-datatable';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName, IconProp } from '@fortawesome/fontawesome-svg-core';
import useOrderNumero, { OrderNumero } from './useOrderNumero';
import useOrderClient, { OrderClient } from './useOrderClient';
import useOrderPrescriber, { OrderPrescripteurs } from './useOrderPrescriber';
import useOrderOffer, { OrderOffer } from './useOrderOffer';

const useOrderFilters = () => {
    const { searchOrderNumero } = useOrderNumero();
    const { searchOrderClient } = useOrderClient();
    const { searchOrderPrescriber } = useOrderPrescriber();
    const { searchOrderOffer } = useOrderOffer();

    const companyFilter: FilterDatatable = {
        type: 'selectApi',
        label: 'Sociétés',
        field: 'clientId',
        icon: 'building-user',
        showResultOnSearchOnly: true,
        searchFunction: searchOrderClient,
        renderItem: (item: OrderClient) => item.libelle as string,
        valueMap: (item: OrderClient) => item.id as unknown as string,
    };

    const orderFilter: SelectApiFilter<OrderNumero> = {
        label: 'Commande',
        field: 'numero',
        type: 'selectApi',
        icon: 'folder-tree',
        searchFunction: searchOrderNumero,
        renderItem: (item: OrderNumero) => item.numero as string,
        valueMap: (item: OrderNumero) => item.numero as string,
    };

    const offerFilter: SelectApiFilter<OrderOffer> = {
        label: 'Offre',
        field: 'offerId',
        icon: 'star',
        type: 'selectApi',
        searchFunction: searchOrderOffer,
        renderItem: (item: OrderOffer) => item.label as string,
        renderIcon: (item: OrderOffer, defaultIcon?: IconName) => (
            <FaOptionIcon icon={['fasl', item?.icon || defaultIcon] as IconProp} />
        ),
        valueMap: (item: OrderOffer) => item.id,
    };

    const prescriberFilter: SelectApiFilter<OrderPrescripteurs> = {
        label: 'Prescripteurs',
        field: 'prescriberId',
        type: 'selectApi',
        icon: 'user',
        searchFunction: searchOrderPrescriber,
        renderItem: (item: OrderPrescripteurs) =>
            item.codeCompta ? `${item.libelle} (${item.codeCompta})` : (item.libelle as string),
        valueMap: (item: OrderPrescripteurs) => item.id as unknown as string,
    };

    const statutInvoceLibelleFilter: StaticSelectFilter = {
        label: 'Facturation',
        field: 'statutInvoceLibelle',
        type: 'staticSelect',
        icon: 'file',
        options: [
            {
                value: 'NOTBILLED',
                label: 'Non facturé',
            },
            {
                value: 'PARTIALLYBILLED',
                label: 'Partiellement facturé',
            },
            {
                value: 'FULLYBILLED',
                label: 'Facturé',
            },
        ],
    };

    const statutQuoteLibelleFilter: StaticSelectFilter = {
        label: 'Devis',
        field: 'statutQuoteLibelle',
        type: 'staticSelect',
        icon: 'file',
        options: [
            {
                value: 'UNQUOTED',
                label: 'Non devisé',
            },
            {
                value: 'QUOTED',
                label: 'Devisé',
            },
        ],
    };

    const incompleteForBillingFilter: FilterDatatable = {
        label: 'Incomplet',
        field: 'incompleteForBilling',
        type: 'boolean',
        icon: 'file',
    };

    const demoFilter: FilterDatatable = {
        label: 'Démo',
        field: 'demo',
        icon: 'flask',
        type: 'boolean',
    };

    const frontOrderFilter: FiltersDatatableList = [companyFilter, orderFilter];

    const backOrderFilter: FiltersDatatableList = [
        orderFilter,
        companyFilter,
        prescriberFilter,
        statutInvoceLibelleFilter,
        statutQuoteLibelleFilter,
        incompleteForBillingFilter,
        offerFilter,
        demoFilter,
    ];

    return { frontOrderFilter, backOrderFilter };
};

export default useOrderFilters;
